import * as Yup from 'yup';
import { isEmpty } from 'lodash-es';
import { DateMode } from '@service-victoria/component-library/components/Date';
import { isDDMMYYYY, isMMYYYY } from './date';
import { constants, date as dateUtils } from 'utils';

export const date = {
  /**
   * Generate a schema for validating a date of birth field
   * @param mode format of the given date ('Full' or 'MonthYear')
   * @param extraAllValidation append extra validations to the date `all` field once all generic fields have been validated successfully
   */
  dateOfBirth: (mode: DateMode, divider = constants.Dates.DATE_DIVIDER_CHAR) => {
    return Yup.mixed()
      .test('day valid', constants.DateValidationErrors.day, function validate(value: string) {
        if (mode === 'FullDate') {
          if (!value) {
            return false;
          }
          const day = value.split(divider)[0];
          return date.sections.day(day);
        }
        return true;
      })
      .test('month valid', constants.DateValidationErrors.month, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const month = mode === 'FullDate' ? parts[1] : parts[0];
        return date.sections.month(month);
      })
      .test('year valid', constants.DateValidationErrors.year, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const year = mode === 'FullDate' ? parts[2] : parts[1];
        return date.sections.year(year);
      })
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value ? false : mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
        }
      )
      .test(
        'minimum date',
        'Sorry, you must be over 15 years old to continue.',
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const maxDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -15, 'y', 'FullDate');
          return dateUtils.compare(`${value}`, maxDate, 'FullDate') <= 0;
        }
      )
      .test(
        'maximum date',
        `That doesn’t look right. Please enter a year after ${
          dateUtils
            .add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate')
            .split(constants.Dates.DATE_DIVIDER_CHAR)[2]
        }`,
        function validate(value: string) {
          if (!value) {
            return false;
          }
          const minDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate');
          return !!value && dateUtils.compare(value, minDate, 'FullDate') >= 0;
        }
      );
  },

  dateOfEvent: (mode: DateMode, divider = constants.Dates.DATE_DIVIDER_CHAR) => {
    return Yup.string()
      .test('day valid', constants.DateValidationErrors.day, function validate(value: string) {
        if (mode === 'FullDate') {
          if (!value) {
            return false;
          }
          const day = value.split(divider)[0];
          return date.sections.day(day);
        }
        return true;
      })
      .test('month valid', constants.DateValidationErrors.month, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const month = mode === 'FullDate' ? parts[1] : parts[0];
        return date.sections.month(month);
      })
      .test('year valid', constants.DateValidationErrors.year, function validate(value: string) {
        if (!value) {
          return false;
        }
        const parts = value.split(divider);
        const year = mode === 'FullDate' ? parts[2] : parts[1];
        return date.sections.year(year);
      })
      .test(
        'isValidDate',
        constants.DateValidationErrors.invalidDate,
        function validate(value: string) {
          return !value ? false : mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
        }
      )
      .test(
        'maximum date',
        `That doesn’t look right. Please enter a year after ${
          dateUtils
            .add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate')
            .split(constants.Dates.DATE_DIVIDER_CHAR)[2]
        }`,
        function validate(value: string) {
          if (!value) {
            return false;
          }
          const minDate = dateUtils.add(dateUtils.NowAsString('FullDate'), -120, 'y', 'FullDate');
          return !!value && dateUtils.compare(value, minDate, 'FullDate') >= 0;
        }
      )
      .test(
        'date is before today',
        'That doesn’t look right. The date of event can’t be before today.',
        function validate(value: string) {
          // Don't validate the maximum if not full date was entered
          if (!value) {
            return false;
          }
          const now = dateUtils.NowAsString('FullDate');
          return !!value && dateUtils.compare(value, now, 'FullDate') >= 0;
        }
      );
  },

  /**
   * Date validatiors functions for any part of the date
   */
  sections: {
    day: (value: string) => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|[12]\d|3[01])/.test(value);
    },
    month: (value: string) => {
      return !isEmpty(value) && value.length === 2 && /(0[1-9]|1[012])/.test(value);
    },
    year: (value: string) => {
      return !isEmpty(value) && value.length === 4;
    },
    all: (mode: DateMode, value: string) => {
      return mode === 'FullDate' ? isDDMMYYYY(value) : isMMYYYY(value);
    }
  }
};

export const generic = {
  isNumber: (val?: unknown) => {
    // empty string is parsed as valid so need to check this edge case
    return val !== '' && !isNaN(Number(val));
  }
};

/**
 * merge multiple schemas together
 * @param schemas collection of schemas
 */
export function mergeSchemas(...schemas: Yup.Schema<unknown>[]) {
  const [first, ...rest] = schemas;

  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);

  return merged;
}

/**
 *  Checks if the string contains the passed array
 */
export const checkStringContains = (string: string, contains: string[]) => {
  let matched = false;

  contains.forEach(item => {
    const cleanString = string.toLowerCase().replace(/[^\w\s]/gi, '');
    if (cleanString.indexOf(item) >= 0) {
      matched = true;
    }
  });

  return matched;
};

/**
 * Transforms mobile number international format.
 * @param phone Mibole number string
 */
export function transformMobileInt(phone: string): string {
  const trimmed = phone.replace(/\s/g, '');
  if (trimmed.substring(0, 2) === '04') {
    return `+61${trimmed.substring(1, trimmed.length)}`;
  }
  return trimmed;
}
